import React, { useState, useEffect } from 'react';
import api from '../api';
import StaffDocuments from '../components/StaffDocuments';

const defaultForm = { name: '', email: '', password: '', role: 'staff', holiday_entitlement: 20, department: '', job_title: '', active: true, date_of_birth: '', stock_take_access: false, sales_board_access: false, crm_access: false, insights_access: false, start_date: '', notes: '' };
const NOTES_MAX = 2000; // the server enforces the same limit

// Job titles drive access. Keep this list in step with the backend (routes/users.js).
const JOB_TITLES = ['Director', 'General Manager', 'Driver', 'Sales Manager', 'Parts Executive', 'Operations'];
const ADMIN_TITLES = ['Director', 'General Manager'];
const accessForTitle = (t) => ADMIN_TITLES.includes(t) ? 'Admin access' : 'Staff access';

const DEPARTMENTS = ['Management', 'Sales', 'Operations', 'Finance', 'Driver'];

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [docsFor, setDocsFor] = useState(null); // person whose Documents modal is open

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/users');
      setUsers(res.data);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(defaultForm); setEditingId(null); setEditingUser(null); setError(''); setModal('create'); };
  const openEdit = (u) => { setForm({ name: u.name, email: u.email, password: '', role: u.role, holiday_entitlement: u.holiday_entitlement, department: u.department || '', job_title: u.job_title || '', active: u.active, date_of_birth: u.date_of_birth ? String(u.date_of_birth).substring(0, 10) : '', stock_take_access: !!u.stock_take_access, sales_board_access: !!u.sales_board_access, crm_access: !!u.crm_access, insights_access: !!u.insights_access, start_date: u.start_date ? String(u.start_date).substring(0, 10) : '', notes: u.notes || '' }); setEditingId(u.id); setEditingUser(u); setError(''); setModal('edit'); };
  const openResetPassword = (u) => { setEditingId(u.id); setEditingUser(u); setNewPassword(''); setError(''); setModal('password'); };
  const openDelete = (u) => { setEditingId(u.id); setEditingUser(u); setError(''); setModal('delete'); };
  const openResend = (u) => { setEditingId(u.id); setEditingUser(u); setNewPassword(''); setError(''); setModal('resend'); };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    if (!form.name || !form.email) return setError('Name and email are required');
    if (!form.job_title) return setError('Please choose a job title (this sets their access level)');
    if (modal === 'create' && !form.password) return setError('Password is required for new users');
    try {
      if (modal === 'create') {
        await api.post('/api/users', { ...form, holiday_entitlement: Number(form.holiday_entitlement) });
        setSuccess(`${form.name} added successfully. Welcome email sent.`);
      } else {
        await api.put(`/api/users/${editingId}`, { ...form, holiday_entitlement: Number(form.holiday_entitlement) });
        setSuccess(`${form.name} updated`);
      }
      setModal(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 8) return setError('Password must be at least 8 characters');
    try {
      await api.post(`/api/users/${editingId}/reset-password`, { newPassword });
      setSuccess('Password reset successfully');
      setModal(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/users/${editingId}`);
      setSuccess(`${editingUser.name} has been deleted`);
      setModal(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleResendWelcome = async () => {
    if (newPassword.length < 8) return setError('Password must be at least 8 characters to resend login details');
    try {
      await api.post(`/api/users/${editingId}/resend-welcome`, { newPassword });
      setSuccess(`Welcome email resent to ${editingUser.email}`);
      setModal(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend welcome email');
    }
  };

  const roleLabels = { staff: 'Staff', director: 'Director', admin: 'Admin' };

  // Admins always have Stock Take by role, so the box shows ticked and locked.
  // Mirrors the backend: an admin job title makes them admin; an unlisted title keeps their current role.
  const isAdminForm = ADMIN_TITLES.includes(form.job_title) || (!JOB_TITLES.includes(form.job_title) && form.role === 'admin');

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Manage Staff</h1>
          <p className="page-subtitle">Add and manage team members and their entitlements</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Add Staff Member</button>
      </div>

      {error && <div className="alert alert-error" onClick={() => setError('')}>{error}</div>}
      {success && <div className="alert alert-success" onClick={() => setSuccess('')}>{success}</div>}

      <div className="card">
        {loading ? (
          <div className="loader">Loading...</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Access</th>
                  <th>Job Title</th>
                  <th>Entitlement</th>
                  <th>Status</th>
                  <th>Stock Take</th>
                  <th>Sales Board</th>
                  <th>CRM</th>
                  <th>Insights</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ opacity: u.active ? 1 : 0.5 }}>
                    <td style={{ fontWeight: '500' }}>{u.name}</td>
                    <td style={{ fontSize: '13px', color: 'var(--grey-mid)' }}>{u.email}</td>
                    <td><span className="badge badge-holiday">{roleLabels[u.role] || u.role}</span></td>
                    <td style={{ fontSize: '13px' }}>{u.job_title || u.department || '-'}</td>
                    <td>{u.holiday_entitlement > 0 ? `${u.holiday_entitlement} days` : <span style={{ color: 'var(--grey-mid)' }}>None</span>}</td>
                    <td><span className={u.active ? 'badge badge-approved' : 'badge badge-declined'}>{u.active ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      {u.role === 'admin'
                        ? <span className="badge badge-approved">On (admin)</span>
                        : u.stock_take_access && u.role !== 'recruit'
                          ? <span className="badge badge-approved">On</span>
                          : <span className="badge badge-cancelled">Off</span>}
                    </td>
                    <td>
                      {u.role === 'admin'
                        ? <span className="badge badge-approved">On (admin)</span>
                        : u.sales_board_access && u.role !== 'recruit'
                          ? <span className="badge badge-approved">On</span>
                          : <span className="badge badge-cancelled">Off</span>}
                    </td>
                    <td>
                      {u.role === 'admin'
                        ? <span className="badge badge-approved">On (admin)</span>
                        : u.crm_access && u.role !== 'recruit'
                          ? <span className="badge badge-approved">On</span>
                          : <span className="badge badge-cancelled">Off</span>}
                    </td>
                    <td>
                      {u.role === 'admin'
                        ? <span className="badge badge-approved">On (admin)</span>
                        : u.insights_access && u.role !== 'recruit'
                          ? <span className="badge badge-approved">On</span>
                          : <span className="badge badge-cancelled">Off</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => openEdit(u)}>Edit</button>
                        <button className="btn btn-outline btn-sm" onClick={() => setDocsFor(u)}>Documents</button>
                        <button className="btn btn-outline btn-sm" onClick={() => openResend(u)}>Resend Login</button>
                        <button className="btn btn-outline btn-sm" onClick={() => openResetPassword(u)}>Reset PW</button>
                        <button className="btn btn-danger btn-sm" onClick={() => openDelete(u)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(modal === 'create' || modal === 'edit') && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-scroll" style={{ maxWidth: '560px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{modal === 'create' ? 'Add Staff Member' : 'Edit Staff Member'}</h3>
              <button className="modal-close" onClick={() => setModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Full Name</label>
                  <input name="name" className="form-control" value={form.name} onChange={handleChange} />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Email Address</label>
                  <input name="email" type="email" className="form-control" value={form.email} onChange={handleChange} />
                </div>
                {modal === 'create' && (
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label className="form-label">Password</label>
                    <input name="password" type="password" className="form-control" value={form.password} onChange={handleChange} placeholder="Minimum 8 characters" />
                  </div>
                )}
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Job Title</label>
                  <select name="job_title" className="form-control" value={form.job_title} onChange={handleChange}>
                    <option value="">Select a job title…</option>
                    {JOB_TITLES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {form.job_title && (
                    <p style={{ fontSize: '11px', color: ADMIN_TITLES.includes(form.job_title) ? 'var(--red)' : 'var(--grey-mid)', marginTop: '4px' }}>
                      Grants <strong>{accessForTitle(form.job_title)}</strong>
                      {ADMIN_TITLES.includes(form.job_title)
                        ? ' — can manage staff and approve absences.'
                        : ' — can request and view their own absences.'}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Holiday Entitlement (days)</label>
                  <input name="holiday_entitlement" type="number" min="0" max="50" step="0.5" className="form-control" value={form.holiday_entitlement} onChange={handleChange} />
                  <p style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '4px' }}>Half days allowed, e.g. 13.5. Set to 0 for no entitlement.</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Department (optional)</label>
                  <select name="department" className="form-control" value={form.department} onChange={handleChange}>
                    <option value="">Select a department…</option>
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date of Birth (optional)</label>
                  <input name="date_of_birth" type="date" className="form-control" value={form.date_of_birth} onChange={handleChange} />
                  <p style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '4px' }}>Shows their birthday on the team calendar (age is never shown).</p>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="staff-start-date">Start Date (optional)</label>
                  <input id="staff-start-date" name="start_date" type="date" className="form-control" value={form.start_date} onChange={handleChange} />
                  <p style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '4px' }}>Shows their work anniversary on the team calendar each year.</p>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: isAdminForm ? 'default' : 'pointer' }}>
                    <input type="checkbox" name="stock_take_access" checked={isAdminForm || form.stock_take_access} disabled={isAdminForm} onChange={handleChange} style={{ accentColor: 'var(--red)', width: '16px', height: '16px' }} />
                    <span className="form-label" style={{ margin: 0 }}>Stock Take access</span>
                  </label>
                  <p style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '4px' }}>
                    {isAdminForm
                      ? 'Admins always have Stock Take access.'
                      : 'Lets this person add, view, delete and export Stock Take entries.'}
                  </p>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: isAdminForm ? 'default' : 'pointer' }}>
                    <input type="checkbox" name="sales_board_access" checked={isAdminForm || form.sales_board_access} disabled={isAdminForm} onChange={handleChange} style={{ accentColor: 'var(--red)', width: '16px', height: '16px' }} />
                    <span className="form-label" style={{ margin: 0 }}>Sales Board access</span>
                  </label>
                  <p style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '4px' }}>
                    {isAdminForm
                      ? 'Admins always see the Sales Board and can change its targets and figures.'
                      : 'Lets this person view the Sales Board and the office screen. Only admins can change targets and figures.'}
                  </p>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: isAdminForm ? 'default' : 'pointer' }}>
                    <input type="checkbox" name="crm_access" checked={isAdminForm || form.crm_access} disabled={isAdminForm} onChange={handleChange} style={{ accentColor: 'var(--red)', width: '16px', height: '16px' }} />
                    <span className="form-label" style={{ margin: 0 }}>CRM access</span>
                  </label>
                  <p style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '4px' }}>
                    {isAdminForm
                      ? 'Admins always have CRM access.'
                      : 'Lets this person see and edit customer records, import and export them. Only switch it on for people who need it.'}
                  </p>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: isAdminForm ? 'default' : 'pointer' }}>
                    <input type="checkbox" name="insights_access" checked={isAdminForm || form.insights_access} disabled={isAdminForm} onChange={handleChange} style={{ accentColor: 'var(--red)', width: '16px', height: '16px' }} />
                    <span className="form-label" style={{ margin: 0 }}>Customer insights access</span>
                  </label>
                  <p style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '4px' }}>
                    {isAdminForm
                      ? 'Admins always have Customer insights.'
                      : 'Lets this person see sales, margins, delivery costs and scores for each customer. Separate from CRM access; margins are sensitive.'}
                  </p>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label" htmlFor="staff-notes">Notes (hobbies, interests)</label>
                  <textarea id="staff-notes" name="notes" className="form-control" rows={4} maxLength={NOTES_MAX} value={form.notes} onChange={handleChange} />
                  <p style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '4px', display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                    <span>Only admins can see these notes. Write them as if the person may read them one day.</span>
                    <span style={{ whiteSpace: 'nowrap' }}>{form.notes.length.toLocaleString('en-GB')}/{NOTES_MAX.toLocaleString('en-GB')}</span>
                  </p>
                </div>
                {modal === 'edit' && (
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                      <input type="checkbox" name="active" checked={form.active} onChange={handleChange} style={{ accentColor: 'var(--red)', width: '16px', height: '16px' }} />
                      <span className="form-label" style={{ margin: 0 }}>Active (uncheck to deactivate without deleting)</span>
                    </label>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>{modal === 'create' ? 'Add Member' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Documents Modal (contracts and letters for this person) */}
      {docsFor && <StaffDocuments person={docsFor} onClose={() => setDocsFor(null)} />}

      {/* Reset Password Modal */}
      {modal === 'password' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Reset Password</h3>
              <button className="modal-close" onClick={() => setModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <p style={{ color: 'var(--grey-mid)', fontSize: '13px', marginBottom: '16px' }}>Resetting password for <strong>{editingUser?.name}</strong></p>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input type="password" className="form-control" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Minimum 8 characters" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleResetPassword}>Reset Password</button>
            </div>
          </div>
        </div>
      )}

      {/* Resend Welcome Email Modal */}
      {modal === 'resend' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Resend Login Details</h3>
              <button className="modal-close" onClick={() => setModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <p style={{ color: 'var(--grey-mid)', fontSize: '13px', marginBottom: '16px' }}>
                This will send a welcome email to <strong>{editingUser?.email}</strong> with their login details. Enter a new password to include in the email.
              </p>
              <div className="form-group">
                <label className="form-label">New Password (will be set and included in email)</label>
                <input type="text" className="form-control" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Minimum 8 characters" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleResendWelcome}>Send Login Details</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {modal === 'delete' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Staff Member</h3>
              <button className="modal-close" onClick={() => setModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <p style={{ color: 'var(--navy)', fontSize: '14px', marginBottom: '8px' }}>
                Are you sure you want to delete <strong>{editingUser?.name}</strong>?
              </p>
              <p style={{ color: 'var(--danger)', fontSize: '13px' }}>
                This will permanently delete their account and all their holiday records. This cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Delete Permanently</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
